'use client';

import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface DrawerProps {
    open: boolean;
    onClose: () => void;
    children: React.ReactNode;
    title?: string;
    side?: 'left' | 'right';
    className?: string;
    size?: 'sm' | 'md' | 'lg' | 'xl';
}

const sizeClasses = {
    sm: 'w-80',
    md: 'w-96',
    lg: 'w-[28rem]',
    xl: 'w-[32rem]'
};

export default function Drawer({
    open,
    onClose,
    children,
    title,
    side = 'right',
    className,
    size = 'md'
}: DrawerProps) {
    // Bloquear scroll del body cuando el drawer esté abierto
    useEffect(() => {
        if (open) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        // Cleanup al desmontar
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [open]);

    // Cerrar con tecla Escape
    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && open) {
                onClose();
            }
        };

        if (open) {
            document.addEventListener('keydown', handleEscape);
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [open, onClose]);

    if (!open) {
        return null;
    }

    return (
        <div className="fixed inset-0 z-50 flex">
            {/* Overlay/Backdrop */}
            <div
                className="fixed inset-0 bg-black/50 transition-opacity"
                onClick={onClose}
                aria-hidden="true"
            />

            {/* Drawer Content */}
            <div
                className={cn(
                    'fixed inset-y-0 flex max-w-full',
                    side === 'right' && 'right-0',
                    side === 'left' && 'left-0'
                )}
            >
                <div
                    className={cn(
                        'relative flex flex-col bg-white shadow-xl',
                        sizeClasses[size],
                        side === 'right' && 'animate-in slide-in-from-right duration-300',
                        side === 'left' && 'animate-in slide-in-from-left duration-300',
                        className
                    )}
                >
                    {/* Header */}
                    {title && (
                        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                            <h2 className="text-lg font-semibold text-gray-900">
                                {title}
                            </h2>
                            <button
                                type="button"
                                className="rounded-md p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-inset"
                                onClick={onClose}
                            >
                                <span className="sr-only">Cerrar</span>
                                <X className="h-5 w-5" aria-hidden="true" />
                            </button>
                        </div>
                    )}

                    {/* Solo botón cerrar si no hay título */}
                    {!title && (
                        <div className="absolute top-4 right-4 z-10">
                            <button
                                type="button"
                                className="rounded-md p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-inset"
                                onClick={onClose}
                            >
                                <span className="sr-only">Cerrar</span>
                                <X className="h-5 w-5" aria-hidden="true" />
                            </button>
                        </div>
                    )}

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto p-4">
                        {children}
                    </div>
                </div>
            </div>
        </div>
    );
}
